import {
  createDisposable,
  Disposable,
  disposedDisposable,
} from "./Disposable";

export type InputMap = object;
export type InputName<Inputs extends InputMap> = Extract<keyof Inputs, string>;
export type RoutedInput<Inputs extends InputMap, Name extends InputName<Inputs>> =
  Inputs[Name] extends Event ? Inputs[Name] : Event;
export type InputHandler<Input extends Event> = (
  input: Input,
) => boolean | void;

export interface InputRouteOptions {
  capture?: boolean;
  passive?: boolean;
  once?: boolean;
  priority?: number;
  signal?: AbortSignal;
}

type RouteRecord = {
  handler: InputHandler<Event>;
  once: boolean;
  priority: number;
  order: number;
  subscription: Disposable;
};

type RouteBucket = {
  target: EventTarget;
  type: string;
  capture: boolean;
  passive: boolean;
  records: RouteRecord[];
  listener: EventListener;
};

export class InputRouter<
  Inputs extends InputMap = GlobalEventHandlersEventMap,
> implements Disposable
{
  private readonly buckets = new Map<EventTarget, Map<string, RouteBucket>>();
  private readonly subscriptions = new Set<Disposable>();
  private nextOrder = 0;
  private _disposed = false;

  constructor(private readonly defaultTarget?: EventTarget) {}

  get disposed(): boolean {
    return this._disposed;
  }

  on<Name extends InputName<Inputs>>(
    type: Name,
    handler: InputHandler<RoutedInput<Inputs, Name>>,
    options: InputRouteOptions = {},
  ): Disposable {
    if (!this.defaultTarget) {
      throw new Error("InputRouter.on requires a default target");
    }
    return this.route(this.defaultTarget, type, handler, options);
  }

  route<Name extends InputName<Inputs>>(
    target: EventTarget,
    type: Name,
    handler: InputHandler<RoutedInput<Inputs, Name>>,
    options: InputRouteOptions = {},
  ): Disposable {
    this.assertActive();
    if (options.signal?.aborted) return disposedDisposable();

    const bucket = this.getOrCreateBucket(target, type, options);
    let abortHandler: (() => void) | undefined;
    const record: RouteRecord = {
      handler: handler as InputHandler<Event>,
      once: options.once ?? false,
      priority: options.priority ?? 0,
      order: this.nextOrder++,
      subscription: undefined as unknown as Disposable,
    };
    const subscription = createDisposable(() => {
      const index = bucket.records.indexOf(record);
      if (index >= 0) bucket.records.splice(index, 1);
      this.subscriptions.delete(subscription);
      if (abortHandler && options.signal) {
        options.signal.removeEventListener("abort", abortHandler);
      }
      if (bucket.records.length === 0) this.removeBucket(bucket);
    });
    record.subscription = subscription;
    bucket.records.push(record);
    bucket.records.sort(
      (left, right) =>
        right.priority - left.priority || left.order - right.order,
    );
    this.subscriptions.add(subscription);

    if (options.signal) {
      abortHandler = () => subscription.dispose();
      options.signal.addEventListener("abort", abortHandler, { once: true });
    }
    return subscription;
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    for (const subscription of [...this.subscriptions]) {
      subscription.dispose();
    }
  }

  private getOrCreateBucket(
    target: EventTarget,
    type: string,
    options: InputRouteOptions,
  ): RouteBucket {
    let targetBuckets = this.buckets.get(target);
    if (!targetBuckets) {
      targetBuckets = new Map();
      this.buckets.set(target, targetBuckets);
    }

    const capture = options.capture ?? false;
    const passive = options.passive ?? false;
    const key = this.bucketKey(type, capture, passive);
    const existing = targetBuckets.get(key);
    if (existing) return existing;

    const bucket: RouteBucket = {
      target,
      type,
      capture,
      passive,
      records: [],
      listener: (() => undefined) as EventListener,
    };
    bucket.listener = (event) => this.dispatch(bucket, event);
    target.addEventListener(type, bucket.listener, { capture, passive });
    targetBuckets.set(key, bucket);
    return bucket;
  }

  private dispatch(bucket: RouteBucket, event: Event): void {
    if (this._disposed) return;
    const errors: unknown[] = [];
    for (const record of [...bucket.records]) {
      if (record.subscription.disposed) continue;
      if (record.once) record.subscription.dispose();
      try {
        if (record.handler(event) === true) break;
      } catch (error) {
        errors.push(error);
      }
    }

    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) {
      throw new AggregateError(errors, `Input routes for "${bucket.type}" failed`);
    }
  }

  private removeBucket(bucket: RouteBucket): void {
    bucket.target.removeEventListener(bucket.type, bucket.listener, {
      capture: bucket.capture,
    });
    const targetBuckets = this.buckets.get(bucket.target);
    targetBuckets?.delete(
      this.bucketKey(bucket.type, bucket.capture, bucket.passive),
    );
    if (targetBuckets?.size === 0) this.buckets.delete(bucket.target);
  }

  private bucketKey(type: string, capture: boolean, passive: boolean): string {
    return `${type}\0${capture ? 1 : 0}${passive ? 1 : 0}`;
  }

  private assertActive(): void {
    if (this._disposed) throw new Error("InputRouter has been disposed");
  }
}
